/**
 * AI test console — enter a prompt, watch the model's answer arrive.
 *
 * Posts to `POST /admin/ai/test/stream`, which resolves the prompt through the
 * exact ModelRouter the runtime uses: same task→alias mapping, same tenant
 * model profile, same fallback chain. So a green result here means the
 * configured routing genuinely works, not that a separate test path works.
 *
 * It streams rather than awaits a completed answer because local inference is
 * slow: on a CPU-only host an 8B model produces ~2 tokens/sec, so a 300-token
 * reply takes minutes. Waiting for the whole thing shows a spinner long past
 * the point a user assumes it has hung; streaming puts the first words on
 * screen in seconds and makes the rest visibly progress.
 *
 * Failures arrive as an SSE `error` event rather than a broken connection — a
 * provider failure is a *result* of the test, and rendering it as an error
 * banner is what makes the console useful for diagnosing a broken model name
 * or a missing credential.
 *
 * Shared by AI Configuration (compact) and AI Playground (with run history),
 * so the two cannot drift apart.
 *
 * Requires `admin:extensions:manage` — a run is a real, billable model call.
 */
import { useEffect, useRef, useState, type JSX, type ReactNode } from 'react'
import { InputTextarea } from 'primereact/inputtextarea'
import { InputNumber } from 'primereact/inputnumber'
import { Dropdown } from 'primereact/dropdown'
import { Check, ChevronDown, ChevronRight, CircleAlert, Eraser, GitCompare, Play, Square, X } from 'lucide-react'

import { apiPostStream, api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import {
  ActionButton,
  Chip,
  DROPDOWN_CLASS,
  DROPDOWN_PANEL_CLASS,
  Field,
  INPUT_CLASS,
} from './ui.js'
import { MODEL_ALIAS_OPTIONS_WITH_DEFAULT } from './alias-options.js'
import PlaygroundCompareDrawer from './PlaygroundCompareDrawer.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AiTestResult {
  ok: boolean
  /** What the router picked before any fallback. */
  requested: { provider: string; model: string; alias: string }
  content?: string
  provider?: string
  model?: string
  inputTokens?: number
  outputTokens?: number
  estimatedCost?: number
  latencyMs?: number
  fellBack?: boolean
  error?: string
}

/** A completed run kept in memory so answers can be compared side by side. */
interface HistoryEntry {
  /** Stable key — server-assigned id when persisted, falling back to a local counter. */
  id: string
  prompt: string
  task: string
  result: AiTestResult
}

/** Shape of a persisted playground run returned by GET /admin/ai/test/runs. */
interface PersistedRun {
  id: string
  task: string | null
  alias: string | null
  systemPrompt: string | null
  prompt: string
  maxTokens: number | null
  status: string
  requestedProviderModel: string | null
  actualProviderModel: string | null
  usedFallback: boolean
  latencyMs: number | null
  promptTokens: number | null
  completionTokens: number | null
  estimatedCost: number | null
  output: string | null
  error: string | null
  dismissed: boolean
  createdAt: string
}

/** Convert a persisted run row into the in-memory HistoryEntry shape. */
function persistedToEntry(r: PersistedRun): HistoryEntry {
  const ok = r.status === 'success'
  const [provider, model] = (r.actualProviderModel ?? r.requestedProviderModel ?? ':').split(':')
  const result: AiTestResult = {
    ok,
    requested: {
      provider: (r.requestedProviderModel ?? ':').split(':')[0] ?? '—',
      model: (r.requestedProviderModel ?? ':').split(':')[1] ?? '—',
      alias: r.alias ?? r.task ?? '',
    },
    fellBack: r.usedFallback,
  }
  if (ok && r.output) result.content = r.output
  if (ok && provider) result.provider = provider
  if (ok && model) result.model = model
  if (r.promptTokens !== null) result.inputTokens = r.promptTokens
  if (r.completionTokens !== null) result.outputTokens = r.completionTokens
  if (r.estimatedCost !== null) result.estimatedCost = r.estimatedCost
  if (r.latencyMs !== null) result.latencyMs = r.latencyMs
  if (!ok && r.error) result.error = r.error
  return {
    id: r.id,
    prompt: r.prompt,
    task: r.task ?? '',
    result,
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Task classes the ModelRouter knows; each maps to a default alias. */
const TASK_OPTIONS = [
  { label: 'Summarize', value: 'summarize' },
  { label: 'Classify', value: 'classify' },
  { label: 'Extract', value: 'extract' },
  { label: 'Agent assist', value: 'agent_assist' },
  { label: 'Customer reply', value: 'customer_reply' },
  { label: 'QA score', value: 'qa_score' },
  { label: 'Voice turn', value: 'voice_turn' },
  { label: 'Translation', value: 'translation' },
]

const DEFAULT_PROMPT = 'Reply with exactly this sentence and nothing else: KC3 AI routing is working.'

/** Matches the endpoint's `maxTokens` default. */
const DEFAULT_MAX_TOKENS = 300

// ---------------------------------------------------------------------------
// Result rendering
// ---------------------------------------------------------------------------

/** Wall-clock seconds, ticking while a run is in flight. */
function useElapsed(active: boolean): number {
  const [elapsed, setElapsed] = useState(0)
  const startedAt = useRef(0)
  useEffect(() => {
    if (!active) return
    startedAt.current = Date.now()
    setElapsed(0)
    const id = setInterval(() => setElapsed(Math.round((Date.now() - startedAt.current) / 1000)), 250)
    return () => clearInterval(id)
  }, [active])
  return elapsed
}

/**
 * Live output while tokens are still arriving.
 *
 * Deliberately separate from ResultPanel: there are no totals to show yet,
 * and the caret plus elapsed counter are what tell the user the run is
 * progressing rather than stuck.
 */
function StreamingPanel({
  text,
  meta,
  elapsed,
}: {
  text: string
  meta: { provider: string; model: string } | null
  elapsed: number
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-sm border border-[#3454cf]/30 bg-[#3454cf]/[0.03]">
      <div className="flex flex-wrap items-center gap-2 border-b border-black/5 px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf]/10 px-2 py-0.5 text-[11px] font-semibold text-[#3454cf]">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3454cf] opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#3454cf]" />
          </span>
          Generating
        </span>
        {meta && (
          <Chip>
            {meta.provider}:{meta.model}
          </Chip>
        )}
        <Chip>{elapsed}s</Chip>
        {text.length > 0 && <Chip>{text.length} chars</Chip>}
      </div>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words px-4 py-3 text-[13px] leading-relaxed text-gray-800">
        {text}
        {/* Caret marks the stream as live even before the first token lands. */}
        <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-[#3454cf] align-text-bottom" />
      </pre>
      {text.length === 0 && (
        <p className="border-t border-black/5 px-4 py-2 text-[11px] text-gray-500">
          Waiting for the first token — a local model that has been idle must load into memory first.
        </p>
      )}
    </div>
  )
}

function ResultPanel({ result }: { result: AiTestResult }): JSX.Element {
  return (
    <div
      className={`overflow-hidden rounded-sm border ${
        result.ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-black/5 px-4 py-2.5">
        <span
          className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-[11px] font-semibold ${
            result.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {result.ok ? <Check className="h-3 w-3" /> : <CircleAlert className="h-3 w-3" />}
          {result.ok ? 'Success' : 'Failed'}
        </span>
        <Chip>
          requested {result.requested.provider}:{result.requested.model}
        </Chip>
        {/* Only meaningful when the primary provider was skipped. */}
        {result.fellBack && (
          <span className="rounded-sm bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
            fell back to {result.provider}:{result.model}
          </span>
        )}
        {result.latencyMs !== undefined && <Chip>{result.latencyMs}ms</Chip>}
        {result.inputTokens !== undefined && (
          <Chip>
            {result.inputTokens} in / {result.outputTokens ?? 0} out
          </Chip>
        )}
        {result.estimatedCost !== undefined && <Chip>~${result.estimatedCost.toFixed(6)}</Chip>}
      </div>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words px-4 py-3 text-[13px] leading-relaxed text-gray-800">
        {result.ok ? result.content : result.error}
      </pre>
    </div>
  )
}

function HistoryRow({
  entry,
  compareMode,
  compareSelected,
  onToggleCompare,
}: {
  entry: HistoryEntry
  compareMode: boolean
  compareSelected: boolean
  onToggleCompare: () => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const { result } = entry
  return (
    <li className={`overflow-hidden rounded-sm border bg-white ${compareSelected ? 'border-[#3454cf] ring-1 ring-[#3454cf]/30' : 'border-gray-200'}`}>
      <div className="flex items-center">
        {compareMode && (
          <button
            type="button"
            onClick={onToggleCompare}
            className={`flex h-full shrink-0 items-center px-2.5 text-xs font-medium transition-colors ${
              compareSelected ? 'bg-[#3454cf] text-white' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
            }`}
            aria-label={compareSelected ? 'Deselect for comparison' : 'Select for comparison'}
          >
            {compareSelected ? '✓' : '○'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-gray-50"
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          )}
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${result.ok ? 'bg-emerald-500' : 'bg-red-500'}`}
            aria-hidden
          />
          <span className="shrink-0 font-mono text-[11px] text-gray-500">
            {result.ok ? `${result.provider}:${result.model}` : result.requested.provider}
          </span>
          <span className="min-w-0 flex-1 truncate text-[13px] text-gray-700">{entry.prompt}</span>
          {result.latencyMs !== undefined && (
            <span className="shrink-0 font-mono text-[11px] text-gray-400">{result.latencyMs}ms</span>
          )}
        </button>
      </div>
      {open && (
        <div className="border-t border-gray-100 px-3 py-3">
          <ResultPanel result={result} />
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Console
// ---------------------------------------------------------------------------

export interface AiTestConsoleProps {
  /**
   * Keep completed runs listed below the result. On for the Playground, off
   * on the config page where the console is a quick "does this work" check.
   */
  showHistory?: boolean
  /** Expose the system-prompt field. The endpoint accepts it either way. */
  showSystemPrompt?: boolean
  /** Textarea height for the main prompt. */
  promptRows?: number
  /**
   * Stacked single-column layout with the drawer field styling (one field
   * per row, uppercase 11px labels). Used when the console is rendered inside
   * a side panel; the default grid layout is unchanged for the Playground.
   */
  stacked?: boolean
}

// Drawer-style field chrome — matches the Guardrails / Agent drawers.
const STACKED_LABEL_CLASS =
  'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'
const STACKED_INPUT_CLASS =
  'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
const STACKED_DROPDOWN_CLASS =
  'w-full !rounded-sm !border-gray-300 !bg-white !shadow-none [&_.p-dropdown-trigger]:!rounded-r-sm [&_.p-dropdown-label]:!rounded-l-sm [&_.p-dropdown-label]:!px-3 [&_.p-dropdown-label]:!py-2 [&_.p-dropdown-label]:!text-xs [&_.p-dropdown-label]:!leading-5 focus-within:!border-[#3454cf] focus-within:!ring-1 focus-within:!ring-[#3454cf]'
const STACKED_DROPDOWN_PANEL_CLASS = '!rounded-sm !border-gray-200 !text-xs'

/** Label wrapper — drawer-style label in stacked mode, shared Field otherwise. */
function FieldRow({
  label,
  stacked,
  children,
}: {
  label: string
  stacked: boolean
  children: ReactNode
}): JSX.Element {
  if (stacked) {
    return (
      <div>
        <label className={STACKED_LABEL_CLASS}>{label}</label>
        {children}
      </div>
    )
  }
  return <Field label={label}>{children}</Field>
}

export default function AiTestConsole({
  showHistory = false,
  showSystemPrompt = false,
  promptRows = 3,
  stacked = false,
}: AiTestConsoleProps): JSX.Element {
  const toast = useToast()

  const [prompt, setPrompt] = useState(DEFAULT_PROMPT)
  const [systemPrompt, setSystemPrompt] = useState('')
  const [task, setTask] = useState('summarize')
  const [alias, setAlias] = useState('')
  const [maxTokens, setMaxTokens] = useState(DEFAULT_MAX_TOKENS)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<AiTestResult | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const localIdRef = useRef(0)

  // Compare-runs state (playground only, when showHistory is on).
  const [compareMode, setCompareMode] = useState(false)
  const [compareIds, setCompareIds] = useState<string[]>([])
  const [isCompareOpen, setIsCompareOpen] = useState(false)

  // Live stream state, cleared once the run settles into `result`.
  const [streamText, setStreamText] = useState('')
  const [streamMeta, setStreamMeta] = useState<{ provider: string; model: string } | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const elapsed = useElapsed(running)

  // Abandoning the page must cancel the generation, not leave the adapter
  // pushing tokens into a socket nobody reads.
  useEffect(() => () => abortRef.current?.abort(), [])

  // Load persisted history on mount so it survives navigation/reload. Only
  // the Playground (showHistory) persists — the config-page compact console
  // stays stateless.
  useEffect(() => {
    if (!showHistory) return
    void (async () => {
      try {
        const res = await api.get<{ runs: PersistedRun[] }>('/admin/ai/test/runs?limit=50')
        if (res.runs?.length) setHistory(res.runs.map(persistedToEntry))
      } catch {
        // Persistence is best-effort — a missing table or auth edge should
        // not block the console from working.
      }
    })()
  }, [showHistory])

  /**
   * Consume the SSE stream, appending each delta to the live panel and
   * settling into a ResultPanel on `done`/`error`.
   *
   * The parser buffers across reads: a network read boundary lands mid-frame
   * often enough that splitting only the current chunk drops tokens.
   */
  async function run(): Promise<void> {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setRunning(true)
    setResult(null)
    setStreamText('')
    setStreamMeta(null)

    // The prompt is captured now: editing the box mid-run must not relabel
    // the history entry this run produces.
    const runPrompt = prompt
    let requested: AiTestResult['requested'] = { provider: '—', model: '—', alias: alias || task }
    let accumulated = ''
    let settled: AiTestResult | null = null

    const finish = (res: AiTestResult): void => {
      settled = res
      setResult(res)
      if (showHistory) {
        // Optimistic local entry — replaced with the server id on persist.
        localIdRef.current += 1
        const localId = `local-${localIdRef.current}`
        setHistory((prev) => [{ id: localId, prompt: runPrompt, task, result: res }, ...prev])

        // Persist after the fact — don't block the UI or complicate the
        // streaming path. Failures are silent: the run still shows locally.
        const requestedModel = `${res.requested.provider}:${res.requested.model}`
        const actualModel = res.provider && res.model ? `${res.provider}:${res.model}` : undefined
        void api
          .post<{ id: string | null }>('/admin/ai/test/runs', {
            task,
            alias: alias || undefined,
            systemPrompt: showSystemPrompt && systemPrompt.trim() ? systemPrompt : undefined,
            prompt: runPrompt,
            maxTokens,
            status: res.ok ? 'success' : 'fail',
            requestedProviderModel: requestedModel,
            actualProviderModel: actualModel,
            usedFallback: res.fellBack ?? false,
            latencyMs: res.latencyMs,
            promptTokens: res.inputTokens,
            completionTokens: res.outputTokens,
            estimatedCost: res.estimatedCost,
            output: res.ok ? res.content : undefined,
            error: res.ok ? undefined : res.error,
          })
          .then((persisted) => {
            if (persisted.id) {
              setHistory((prev) =>
                prev.map((h) => (h.id === localId ? { ...h, id: persisted.id as string } : h)),
              )
            }
          })
          .catch(() => {
            /* best-effort persistence */
          })
      }
    }

    try {
      const response = await apiPostStream(
        '/admin/ai/test/stream',
        {
          prompt: runPrompt,
          task,
          maxTokens,
          ...(alias ? { alias } : {}),
          ...(showSystemPrompt && systemPrompt.trim() ? { systemPrompt } : {}),
        },
        controller.signal,
      )

      if (!response.body) throw new Error('Streaming is not supported by this connection')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // SSE frames are separated by a blank line.
        const frames = buffer.split('\n\n')
        buffer = frames.pop() ?? ''

        for (const frame of frames) {
          let event = 'message'
          let data = ''
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim()
            else if (line.startsWith('data:')) data += line.slice(5).trim()
          }
          if (!data) continue

          const payload = JSON.parse(data) as Record<string, unknown>

          if (event === 'meta') {
            requested = payload.requested as AiTestResult['requested']
            setStreamMeta({ provider: requested.provider, model: requested.model })
          } else if (event === 'delta') {
            accumulated += String(payload.text ?? '')
            setStreamText(accumulated)
          } else if (event === 'done') {
            finish({ ...(payload as unknown as AiTestResult), ok: true, requested })
          } else if (event === 'error') {
            finish({ ok: false, requested, error: String(payload.error ?? 'Unknown error') })
          }
        }
      }

      // Stream closed without a terminal frame — a proxy timeout or a worker
      // restart. Keep whatever text arrived rather than discarding it.
      if (!settled) {
        finish(
          accumulated
            ? { ok: true, requested, content: accumulated }
            : { ok: false, requested, error: 'The stream closed before the model produced a response.' },
        )
      }
    } catch (err) {
      // A user-initiated stop is not a failure — leave the partial text as-is.
      if (controller.signal.aborted) {
        if (accumulated) finish({ ok: true, requested, content: accumulated })
      } else {
        toast.error('Test request failed', err instanceof Error ? err.message : String(err))
        finish({ ok: false, requested, error: err instanceof Error ? err.message : String(err) })
      }
    } finally {
      setRunning(false)
      setStreamText('')
      setStreamMeta(null)
      abortRef.current = null
    }
  }

  function stop(): void {
    abortRef.current?.abort()
  }

  return (
    <>
      <div className={stacked ? 'mb-4 flex flex-col gap-4' : 'mb-4 grid grid-cols-1 gap-4 md:grid-cols-3'}>
        <FieldRow label="Task" stacked={stacked}>
          <Dropdown
            value={task}
            options={TASK_OPTIONS}
            // Without optionValue, PrimeReact hands back the whole
            // { label, value } option object, not the value.
            optionLabel="label"
            optionValue="value"
            onChange={(e: { value: string }) => setTask(e.value)}
            disabled={running}
            className={stacked ? STACKED_DROPDOWN_CLASS : DROPDOWN_CLASS}
            panelClassName={stacked ? STACKED_DROPDOWN_PANEL_CLASS : DROPDOWN_PANEL_CLASS}
          />
        </FieldRow>
        <FieldRow label="Alias override" stacked={stacked}>
          <Dropdown
            value={alias}
            options={[...MODEL_ALIAS_OPTIONS_WITH_DEFAULT]}
            optionLabel="label"
            optionValue="value"
            onChange={(e: { value: string }) => setAlias(e.value)}
            disabled={running}
            className={stacked ? STACKED_DROPDOWN_CLASS : DROPDOWN_CLASS}
            panelClassName={stacked ? STACKED_DROPDOWN_PANEL_CLASS : DROPDOWN_PANEL_CLASS}
          />
        </FieldRow>
        <FieldRow label="Max tokens" stacked={stacked}>
          <InputNumber
            value={maxTokens}
            onValueChange={(e) => setMaxTokens(e.value ?? DEFAULT_MAX_TOKENS)}
            min={1}
            max={4000}
            disabled={running}
            inputClassName={stacked ? STACKED_INPUT_CLASS : INPUT_CLASS}
            className="w-full"
          />
        </FieldRow>
      </div>

      {showSystemPrompt && (
        <div className="mb-4">
          <FieldRow label="System prompt (optional)" stacked={stacked}>
            <InputTextarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={2}
              autoResize
              disabled={running}
              placeholder="You are a concise contact-centre assistant."
              className={stacked ? STACKED_INPUT_CLASS : INPUT_CLASS}
            />
          </FieldRow>
        </div>
      )}

      <FieldRow label="Prompt" stacked={stacked}>
        <InputTextarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={promptRows}
          autoResize
          disabled={running}
          className={stacked ? STACKED_INPUT_CLASS : INPUT_CLASS}
        />
      </FieldRow>

      <div className="mt-4 flex items-center gap-2">
        <ActionButton
          tone="primary"
          icon={<Play className="h-4 w-4" />}
          label={running ? `Generating… ${elapsed}s` : 'Run test'}
          busy={running}
          disabled={prompt.trim().length === 0}
          onClick={() => void run()}
        />
        {running && (
          <ActionButton tone="secondary" icon={<Square className="h-4 w-4" />} label="Stop" onClick={stop} />
        )}
        {showHistory && !running && history.length > 0 && (
          <>
            <ActionButton
              tone="ghost"
              icon={<Eraser className="h-4 w-4" />}
              label="Clear history"
              onClick={() => {
                // Soft-dismiss server-side so cost/usage audit data survives;
                // clear locally so the list empties immediately.
                void api.post('/admin/ai/test/runs/clear').catch(() => {
                  /* best-effort */
                })
                setHistory([])
                setResult(null)
                setCompareMode(false)
                setCompareIds([])
              }}
            />
            {history.length >= 2 && (
              compareMode ? (
                <>
                  <span className="text-xs text-gray-500">{compareIds.length}/2 selected</span>
                  <ActionButton
                    tone="primary"
                    icon={<GitCompare className="h-4 w-4" />}
                    label="Compare"
                    disabled={compareIds.length !== 2}
                    onClick={() => setIsCompareOpen(true)}
                  />
                  <ActionButton
                    tone="ghost"
                    icon={<X className="h-4 w-4" />}
                    label="Exit"
                    onClick={() => { setCompareMode(false); setCompareIds([]) }}
                  />
                </>
              ) : (
                <ActionButton
                  tone="ghost"
                  icon={<GitCompare className="h-4 w-4" />}
                  label="Compare runs"
                  onClick={() => { setCompareMode(true); setCompareIds([]) }}
                />
              )
            )}
          </>
        )}
      </div>

      {running && (
        <div className="mt-4">
          <StreamingPanel text={streamText} meta={streamMeta} elapsed={elapsed} />
        </div>
      )}

      {!running && result && (
        <div className="mt-4">
          <ResultPanel result={result} />
        </div>
      )}

      {showHistory && history.length > 0 && (
        <div className="mt-5 border-t border-gray-100 pt-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Recent runs ({history.length})
          </p>
          <ul className="flex flex-col gap-1.5">
            {history.map((entry) => (
              <HistoryRow
                key={entry.id}
                entry={entry}
                compareMode={compareMode}
                compareSelected={compareIds.includes(entry.id)}
                onToggleCompare={() => {
                  setCompareIds((prev) => {
                    if (prev.includes(entry.id)) return prev.filter((id) => id !== entry.id)
                    if (prev.length >= 2) return [prev[1]!, entry.id]
                    return [...prev, entry.id]
                  })
                }}
              />
            ))}
          </ul>
        </div>
      )}

      {/* Playground run comparison drawer */}
      {showHistory && (() => {
        const runA = history.find((h) => h.id === compareIds[0])
        const runB = history.find((h) => h.id === compareIds[1])
        if (!runA || !runB) return null
        const toCompareRun = (e: HistoryEntry) => ({
          id: e.id,
          task: e.task || null,
          alias: e.result.requested.alias || null,
          prompt: e.prompt,
          systemPrompt: null,
          maxTokens: null,
          status: e.result.ok ? 'success' : 'fail',
          requestedProviderModel: `${e.result.requested.provider}:${e.result.requested.model}`,
          actualProviderModel: e.result.provider && e.result.model ? `${e.result.provider}:${e.result.model}` : null,
          usedFallback: e.result.fellBack ?? false,
          latencyMs: e.result.latencyMs ?? null,
          promptTokens: e.result.inputTokens ?? null,
          completionTokens: e.result.outputTokens ?? null,
          estimatedCost: e.result.estimatedCost ?? null,
          output: e.result.content ?? null,
          error: e.result.error ?? null,
          createdAt: new Date().toISOString(),
        })
        return (
          <PlaygroundCompareDrawer
            runA={toCompareRun(runA)}
            runB={toCompareRun(runB)}
            isOpen={isCompareOpen}
            onClose={() => setIsCompareOpen(false)}
          />
        )
      })()}
    </>
  )
}
